import { connect } from "react-redux";
import React, { Component, Fragment } from "react";
import Select, { components } from "react-select";
import { withRouter } from "react-router-dom";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import { admin_bp, provider_case_manager, provider_peer } from "@/config/roles";
import { adminRequests } from "@/api/(requests)/admin";
import { BackToPatientProfile } from "@/components/Common/BackToPatientProfile";
import { Select as BasicSelect } from "@/components/UI/StyledInput";
import { Button2 } from "@/ui2/atoms/Button";
import Circle from "@/components/UI/Circle";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getQueryParams } from "@/utils/filters";
import images from "@/utils/images";
import { isAuthorized } from "@/utils/authToken";
import {
  List,
  ListBody,
  ListHeader,
  ListItem,
  ListTitleBar,
  TitleBarItem,
} from "@/components/UI/ListView";
import { MemberHeader } from "@/features/notes/(v2)/components/MemberHeader";
import SearchBar from "@/components/UI/SearchBar";
import { sharedRequests } from "@/api/(requests)/shared";
import store from "@/redux/store";

import "@/components/Provider/PatientView/styles.scss";

class CareTeamList extends Component {
  show_popup = getQueryParams("showAddMember", this.props.location.search);
  isCurrentUserAdmin =
    isAuthorized("admin:customer_success_executive") ||
    isAuthorized("admin:customer_success_manager") ||
    isAuthorized("admin:admin");
  isCurrentUserBHS = isAuthorized("provider:behavioral");

  constructor(props) {
    super(props);
    this.state = {
      careTeam: "",
      careTeamProviderIds: [],
      isdisabled: true,
      patientId: this.props.match.params.patientId,
      patientInsurance: [],
      providerName: null,
      providerNameOptions: null,
      providers: null,
      providerType: "",
      providerTypeOptions: null,
      search: "",
      showPopUp: this.show_popup === "true",
    };
  }

  componentDidMount() {
    this.getPatientDetails();
    this.getCareTeamMemberList();
  }

  getProviderList = () => {
    // This line accomplishes multiple things
    // it temporarily hides all the calendar options and prevents peers/case managers
    // access to an endpoint they may not need
    if (isAuthorized(provider_case_manager) || isAuthorized(provider_peer)) {
      return;
    }

    let searchParams = { insurance: this.state.patientInsurance.join(",") };

    if (!(this.isCurrentUserAdmin || this.props.profile?.provider_type === "BEHAVIORAL_INTAKE")) {
      searchParams["provider_type"] = "PRESCRIBE";
    }

    sharedRequests
      .fetch_providers({ searchParams })
      .then((response) => {
        if (response && response["providers"]) {
          let providers = response["providers"];

          providers = providers.filter(
            (provider) => !this.state.careTeamProviderIds.includes(provider["username"]),
          );

          this.setState({
            providerNameOptions: [],
            providers,
            providerTypeOptions: this.getProviderTypeOptions(),
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  getPatientDetails = () => {
    let urlParams = { patientId: this.state.patientId };

    sharedRequests
      .fetch_patient_details({ context: { showLoader: false }, urlParams })
      .then((response) => {
        let newState = { patientInsurance: [] };

        if (response && response.user) {
          const patient = response.user;

          if (patient.primary_insurance && patient.primary_insurance.carrier) {
            newState.patientInsurance.push(patient.primary_insurance.carrier);
          }

          if (
            patient.secondary_insurance &&
            patient.secondary_insurance.carrier &&
            !newState.patientInsurance.includes(patient.secondary_insurance.carrier)
          ) {
            newState.patientInsurance.push(patient.secondary_insurance.carrier);
          }
        }

        this.setState(newState);
        this.getProviderList();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  getCareTeamMemberList = (refresh = false) => {
    let urlParams = { patientId: this.state.patientId };

    sharedRequests
      .fetch_patient_careteam({ urlParams })
      .then((response) => {
        if (response) {
          const careTeam = response;
          const careTeamProviderIds = careTeam.map((member) => member["provider_id"]);

          this.setState(
            {
              careTeam: careTeam,
              careTeamProviderIds: careTeamProviderIds,
            },
            () => {
              if (refresh) {
                this.setState({ providerType: "" });
                this.getProviderList();
              }
            },
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  addTeamMember = async () => {
    const urlParams = { patientId: this.state.patientId };
    const searchParams = { providerId: this.state.providerName };
    const params = { searchParams, urlParams };

    sharedRequests
      .add_provider_to_care_team(params)
      .then(() => {
        if (
          this.state.providerType === "BEHAVIORAL" &&
          this.doesCareTeamHaveProviderType("Intake Behavioral Specialist")
        ) {
          store.dispatch(
            addToast({
              message: "Behavioral Specialist added. Intake removed automatically.",
              type: toastMessageTypes.success,
            }),
          );
        }

        this.setState({
          showPopUp: false,
        });

        this.getCareTeamMemberList(true);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  deleteProviderFromCareTeam = async (providerId) => {
    const urlParams = { patientId: this.state.patientId };
    const searchParams = { providerId: providerId };
    const params = { searchParams, urlParams };

    try {
      await adminRequests.delete_provider_from_care_team(params);
      this.getCareTeamMemberList(true);
    } catch (e) {
      console.log(e);
    }
  };

  doesCareTeamHaveProviderType = (care_team_provider_type) =>
    this.state.careTeam.some((member) => member.type === care_team_provider_type);

  getProviderTypeOptions = () => {
    if (this.isCurrentUserAdmin) {
      const doesCareTeamHaveBHS = this.doesCareTeamHaveProviderType("Behavioral Specialist");

      return [
        { display: "PCP", value: "PCP" },
        { display: "Care Manager", value: "CARE_MANAGER" },
        { display: "Behavioral Specialist", value: "BEHAVIORAL" },
        {
          disabled: doesCareTeamHaveBHS,
          display: "Intake Behavioral Specialist",
          toolTip: doesCareTeamHaveBHS
            ? {
                id: "test",
                text: "Intakes cannot be added while a BHS is on the care team",
              }
            : undefined,
          value: "BEHAVIORAL_INTAKE",
        },
        { display: "Prescriber", value: "PRESCRIBE" },
        { display: "Peer", value: "PEER" },
        { display: "Case Manager", value: "CASE_MANAGER" },
      ];
    } else if (this.props.profile?.provider_type === "BEHAVIORAL_INTAKE") {
      return [
        { display: "Behavioral Specialist", value: "BEHAVIORAL" },
        { display: "Prescriber", value: "PRESCRIBE" },
      ];
    } else {
      return [{ display: "Prescriber", value: "PRESCRIBE" }];
    }
  };

  setProviderNameOptions = (namesArray) => {
    return namesArray.map((el) => {
      return {
        accept_new_patients: el.accept_new_patients,
        label: el.name,
        value: el["username"],
      };
    });
  };

  providerTypeChangeHandler = (e) => {
    let typeValue = e.target.value;
    let providers = this.state.providers;

    if (providers) {
      providers = providers.filter((provider) => provider["provider_type"] === typeValue);
    }

    this.setState({
      providerNameOptions: this.setProviderNameOptions(providers),
      providerType: typeValue,
      providerTypeOptions: this.getProviderTypeOptions(),
    });
  };

  providerNameChangeHandler = (e) => {
    let nameValue = e.value;

    this.setState({
      isdisabled: false,
      providerName: nameValue,
    });
  };

  renderControls = () => {
    return (
      <div className="tw:flex tw:gap-2">
        <SearchBar className="mr-lg-2" onchange={this.updateSearch} value={this.state.search} />

        {!isAuthorized("provider:pcp") &&
          !isAuthorized(provider_case_manager) &&
          !isAuthorized(provider_peer) && (
            <Button2
              isDisabled={isAuthorized(admin_bp) || !this.state.providers}
              isLoading={!this.state.providers}
              onPress={this.showPopup}
            >
              Add Member
            </Button2>
          )}
      </div>
    );
  };

  updateSearch = (e) => {
    this.setState({ search: e.target.value });
  };

  showPopup = () => {
    this.setState({
      isdisabled: true,
      providerName: null,
      showPopUp: true,
    });
  };

  cancelPopup = () => {
    this.setState({
      showPopUp: false,
    });
  };

  isScheduleActiveForProviderType(type) {
    return (
      (this.isCurrentUserAdmin &&
        ["Behavioral Specialist", "Intake Behavioral Specialist", "Prescriber"].includes(type)) ||
      (this.isCurrentUserBHS && ["Behavioral Specialist", "Prescriber"].includes(type))
    );
  }

  render() {
    let filteredMembers = this.state.careTeam;

    if (filteredMembers) {
      filteredMembers = this.state.careTeam.filter((team) => {
        let firstName = team.name.split(" ")[0];
        let lastName = team.name.split(" ")[1];
        let filteredpatientsFullName;
        let filteredpatientsFN;
        let filteredpatientsLN;
        let primaryFilter = this.state.search.replace(
          new RegExp("[.\\\\+*?\\[\\^\\]$(){}=!<>|:\\-]", "g"),
          "\\$&",
        );

        filteredpatientsFullName = team.name.match(
          new RegExp("^" + primaryFilter.toLowerCase(), "i"),
        );

        filteredpatientsFN = firstName.match(new RegExp("^" + primaryFilter.toLowerCase(), "i"));

        if (lastName) {
          filteredpatientsLN = lastName.match(new RegExp("^" + primaryFilter.toLowerCase(), "i"));
        }

        return filteredpatientsFullName || filteredpatientsFN || filteredpatientsLN;
      });
    }

    let listItems = (
      <Fragment>
        {filteredMembers &&
          filteredMembers.map((team) => {
            return (
              <ListItem key={team.email}>
                {/* for mobile devices */}
                <div className="d-flex d-lg-none justify-content-between">
                  <div className="w-75 d-flex fs-14 flex-column">
                    <span>{team.name}</span>
                    <span>{team.type}</span>
                    <span>{team.email}</span>
                  </div>

                  {(this.isCurrentUserAdmin ||
                    (this.props.profile?.provider_type === "BEHAVIORAL_INTAKE" &&
                      ["Behavioral Specialist", "Prescriber"].includes(team.type))) && (
                    <div className="d-flex fs-14 flex-column">
                      <button
                        className="fs-16 mobile-item-link tw:border-0 tw:bg-transparent tw:p-0 tw:font-bold"
                        onClick={() => this.deleteProviderFromCareTeam(team.provider_id)}
                        type="button"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* for desktop */}
                <div
                  className="no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between"
                  {...getAutomationDataAttr("CareTeamMemberListItem")}
                >
                  <span className="col-3">{team.name}</span>
                  <span className="col-3">{team.type}</span>
                  <span className="col-4">{team.email}</span>

                  {this.isScheduleActiveForProviderType(team.type) ? (
                    <div className="col-1 d-flex d-flex tw:justify-center">
                      <div className="cursor">
                        <button
                          className="tw:border-0 tw:bg-transparent tw:p-0"
                          onClick={() =>
                            this.props.history.push(
                              "/app/schedule/" +
                                team.provider_id +
                                "?patientId=" +
                                this.state.patientId +
                                "&providerName=" +
                                team.name +
                                "&providerType=" +
                                team.type,
                            )
                          }
                          type="button"
                          {...getAutomationDataAttr(`ScheduleCareTeamMember_${team.email}`)}
                        >
                          <img
                            alt="Schedule care team member"
                            src={images("./icons/calender-icon.svg")}
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="col-1" />
                  )}

                  {(this.isCurrentUserAdmin ||
                    (this.props.profile?.provider_type === "BEHAVIORAL_INTAKE" &&
                      ["Behavioral Specialist", "Prescriber"].includes(team.type))) && (
                    <div className="col-1 d-flex d-flex tw:justify-center">
                      <div className="cursor">
                        <button
                          className="tw:border-0 tw:bg-transparent tw:p-0"
                          onClick={() => this.deleteProviderFromCareTeam(team.provider_id)}
                          type="button"
                          {...getAutomationDataAttr("DeleteCareTeamMember")}
                        >
                          <img alt="Delete care team member" src={images("./icons/delete.svg")} />
                        </button>
                      </div>
                    </div>
                  )}

                  {(!this.isCurrentUserAdmin ||
                    !this.isCurrentUserBHS ||
                    (this.isCurrentUserBHS && team.type !== "Prescriber")) && (
                    <div className="col-1" />
                  )}
                </div>
              </ListItem>
            );
          })}
      </Fragment>
    );

    let AddMemberPopup = (
      <Fragment>
        <div className="addMemberPopup position-fixed d-flex align-items-center tw:justify-center">
          <div className="addMemberPopup__inner d-flex flex-column position-absolute px-4">
            <h2 className="fs-17 mt-4 text-center tw:font-bold">Add Member</h2>
            <span className="fs-15 my-2 mt-1 pt-1 d-block tw:font-bold">Select Provider Type</span>
            <BasicSelect
              changeHandler={this.providerTypeChangeHandler}
              name="provider_type"
              options={this.state.providerTypeOptions}
              testId="AddCareTeamMemberProviderTypeDropdown"
              value={this.state.providerType}
            />

            <span className="fs-15 my-2 mt-1 pt-1 d-block tw:font-bold">Select Member</span>

            {this.state.providerNameOptions && (
              <Select
                classNamePrefix="select"
                components={{ Option }}
                name="provider_type"
                onChange={this.providerNameChangeHandler}
                options={this.state.providerNameOptions}
                placeholder="Select Name"
                {...getAutomationDataAttr("AddCareTeamMemberProviderNameDropdown", "id")}
              />
            )}

            {this.state.providerType === "BEHAVIORAL" &&
              this.doesCareTeamHaveProviderType("Intake Behavioral Specialist") && (
                <div className="Intake-removal-warning">
                  <span>
                    Adding a BHS will automatically remove the intake provider from the care team.
                  </span>
                </div>
              )}

            <div className="tw:my-4 tw:grid tw:grid-cols-2 tw:gap-5">
              <Button2 onPress={this.cancelPopup} variant="secondary">
                CANCEL
              </Button2>

              <Button2 isDisabled={this.state.isdisabled} onPress={this.addTeamMember}>
                Add
              </Button2>
            </div>
          </div>
        </div>
      </Fragment>
    );

    return (
      <div>
        <BackToPatientProfile patientId={this.state.patientId} />

        <div className="d-flex tw:justify-center">
          <MemberHeader patientId={this.state.patientId} />
        </div>

        {this.state.showPopUp && this.state.providerTypeOptions && AddMemberPopup}

        <ListBody>
          <ListHeader render={this.renderControls} title="Care Team" />
          <ListTitleBar>
            <TitleBarItem classes="col-3" text="Name" />
            <TitleBarItem classes="col-3" text="Provider Type" />
            <TitleBarItem classes="col-4" text="Email Address" />
            <TitleBarItem classes="col-1" text="" />

            {this.isCurrentUserAdmin && <TitleBarItem classes="col-1" text="" />}
          </ListTitleBar>

          <List>{this.state.careTeam && listItems}</List>
        </ListBody>
      </div>
    );
  }
}

const Option = (props) => {
  const option = props.data;
  const circleType = option.accept_new_patients ? "green" : "red";

  return (
    <components.Option {...props}>
      {option.label} <Circle classes="float-right" type={circleType}></Circle>
    </components.Option>
  );
};

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

export default withRouter(connect(mapStateToProps)(CareTeamList));
